import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiUrl } from "../api";
import { haptic } from "../telegram";
import type { ChatMessage, ConversationItem } from "../types";
import { useBackNavigation } from "../use-back-navigation";
import { ConversationProfile } from "./ProfilePreview";
import { PageDialog } from "./ReferenceUI";
import { ReportDialog } from "./ReportDialog";

function mergeMessages(current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming) byId.set(message.id, message);
  return [...byId.values()].sort((a, b) => {
    const timeOrder = a.createdAt.localeCompare(b.createdAt);
    return timeOrder !== 0 ? timeOrder : a.id.localeCompare(b.id);
  });
}

export function Chat({ conversation, onBack }: { conversation: ConversationItem; onBack(): void }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [online, setOnline] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const pendingSend = useRef<{ clientMessageId: string; body: string } | null>(null);
  useBackNavigation(onBack);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const olderLoaded = useRef(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const shouldScroll = useRef(true);

  const markRead = useCallback(
    async (messageId: string) => {
      if (document.visibilityState !== "visible") return;
      await api(`/v1/conversations/${conversation.id}/read`, {
        method: "POST",
        body: JSON.stringify({ messageId }),
      }).catch(() => undefined);
    },
    [conversation.id],
  );

  const loadLatest = useCallback(async () => {
    try {
      const result = await api<{ messages: ChatMessage[]; nextCursor: string | null }>(
        `/v1/conversations/${conversation.id}/messages?limit=50`,
      );
      const history = historyRef.current;
      shouldScroll.current = !history || history.scrollHeight - history.scrollTop - history.clientHeight < 80;
      setMessages((current) => mergeMessages(current, result.messages));
      if (!olderLoaded.current) setNextCursor(result.nextCursor);
      const latest = result.messages.at(-1);
      if (latest) await markRead(latest.id);
      setError("");
    } catch {
      setError("Не удалось обновить сообщения.");
    } finally {
      setLoading(false);
    }
  }, [conversation.id, markRead]);

  useEffect(() => {
    olderLoaded.current = false;
    setMessages([]);
    setNextCursor(null);
    setLoading(true);
    void loadLatest();

    const source = new EventSource(apiUrl(`/v1/conversations/${conversation.id}/events`), {
      withCredentials: true,
    });
    source.addEventListener("ready", () => {
      void loadLatest();
    });
    const refreshTimer = window.setInterval(() => {
      if (document.visibilityState === "visible") void loadLatest();
    }, 30_000);
    source.addEventListener("message", () => {
      void loadLatest();
    });

    const visibilityHandler = () => {
      if (document.visibilityState === "visible") void loadLatest();
    };
    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      window.clearInterval(refreshTimer);
      source.close();
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, [conversation.id, loadLatest]);

  useEffect(() => {
    let active = true;
    setOnline(null);
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void api<{ online: boolean | null }>(`/v1/conversations/${conversation.id}/presence`)
        .then((value) => {
          if (active) setOnline(value.online ?? null);
        })
        .catch(() => {
          if (active) setOnline(null);
        });
    };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [conversation.id]);

  useEffect(() => {
    if (!shouldScroll.current) return;
    bottomRef.current?.scrollIntoView({ block: "end" });
    shouldScroll.current = false;
  });

  const loadOlder = async () => {
    if (!nextCursor || loadingOlder) return;
    setLoadingOlder(true);
    setError("");
    try {
      const result = await api<{ messages: ChatMessage[]; nextCursor: string | null }>(
        `/v1/conversations/${conversation.id}/messages?limit=50&before=${encodeURIComponent(nextCursor)}`,
      );
      const history = historyRef.current;
      const oldHeight = history?.scrollHeight ?? 0;
      const oldTop = history?.scrollTop ?? 0;
      olderLoaded.current = true;
      shouldScroll.current = false;
      setMessages((current) => mergeMessages(current, result.messages));
      setNextCursor(result.nextCursor);
      requestAnimationFrame(() => {
        if (history) history.scrollTop = oldTop + history.scrollHeight - oldHeight;
      });
    } catch {
      setError("Не удалось загрузить более ранние сообщения. Попробуйте ещё раз.");
    } finally {
      setLoadingOlder(false);
    }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;

    const existingPending = pendingSend.current;
    const request =
      existingPending?.body === body
        ? existingPending
        : {
            clientMessageId: crypto.randomUUID(),
            body,
          };
    pendingSend.current = request;
    setSending(true);
    setError("");

    try {
      const result = await api<{ message: ChatMessage }>(`/v1/conversations/${conversation.id}/messages`, {
        method: "POST",
        body: JSON.stringify(request),
      });
      pendingSend.current = null;
      shouldScroll.current = true;
      setMessages((current) => mergeMessages(current, [result.message]));
      setDraft((current) => (current.trim() === body ? "" : current));
      haptic("success");
    } catch {
      setError("Сообщение не отправлено. Повторная попытка не создаст дубль.");
    } finally {
      setSending(false);
    }
  };

  const removePair = async () => {
    if (removing) return;
    setRemoving(true);
    setError("");
    try {
      await api(`/v1/matches/${conversation.matchId}`, { method: "DELETE" });
      onBack();
    } catch {
      setError("Не удалось удалить пару. Попробуйте ещё раз.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <section className="chat-screen">
      <header className="chat-header">
        <button type="button" className="chat-back" aria-label="Назад" onClick={onBack}>
          ‹
        </button>
        <button
          type="button"
          className="chat-person"
          aria-label={`Открыть анкету: ${conversation.user.displayName}`}
          onClick={() => setProfileOpen(true)}
        >
          {conversation.user.photoUrl ? (
            <img src={conversation.user.photoUrl} alt="" />
          ) : (
            <span className="chat-avatar-fallback" />
          )}
          <span>
            <strong>{conversation.user.displayName}</strong>
            <span>{online === true ? "В сети" : online === false ? "Не в сети" : "Взаимная симпатия"}</span>
          </span>
        </button>
        <button type="button" className="chat-more" aria-label="Безопасность" onClick={() => setSafetyOpen(true)}>
          •••
        </button>
      </header>

      <div ref={historyRef} className="chat-history" aria-live="polite">
        {nextCursor && (
          <button type="button" className="load-older" disabled={loadingOlder} onClick={loadOlder}>
            {loadingOlder ? "Загружаем…" : "Показать более ранние"}
          </button>
        )}
        {loading && messages.length === 0 ? (
          <div className="chat-loading">Загружаем сообщения…</div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">
            <span>♥</span>
            <strong>Начните разговор</strong>
            <p>У вас взаимная симпатия. Можно написать первое сообщение.</p>
          </div>
        ) : (
          messages.map((message) => (
            <div className={message.mine ? "message-row mine" : "message-row"} key={message.id}>
              <div className="message-bubble">
                <p>{message.body}</p>
                <span>
                  {new Date(message.createdAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                  {message.mine && (message.readByOther ? " · прочитано" : " · отправлено")}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {error && (
        <div className="chat-error" role="alert">
          {error}
          <button type="button" className="secondary-button" onClick={() => void loadLatest()}>
            Обновить сообщения
          </button>
        </div>
      )}

      <div className="chat-composer">
        <textarea
          rows={1}
          maxLength={4000}
          value={draft}
          placeholder="Сообщение"
          aria-label="Сообщение"
          onChange={(event) => {
            setDraft(event.target.value);
            if (pendingSend.current?.body !== event.target.value.trim()) pendingSend.current = null;
          }}
        />
        <button type="button" disabled={sending || !draft.trim()} aria-label="Отправить" onClick={send}>
          ↑
        </button>
      </div>

      {profileOpen && <ConversationProfile conversationId={conversation.id} onClose={() => setProfileOpen(false)} />}
      {safetyOpen && (
        <PageDialog
          title={confirmRemove ? "Удалить из пар?" : "Меню чата"}
          onClose={() => {
            setSafetyOpen(false);
            setConfirmRemove(false);
          }}
          busy={removing}
        >
          {confirmRemove ? (
            <>
              <p>Совпадение и переписка будут удалены у обоих участников. Отменить это действие нельзя.</p>
              <button type="button" className="reference-primary" disabled={removing} onClick={() => void removePair()}>
                {removing ? "Удаляем…" : "Удалить пару"}
              </button>
              <button
                type="button"
                className="reference-row"
                disabled={removing}
                onClick={() => setConfirmRemove(false)}
              >
                Отмена
              </button>
            </>
          ) : (
            <div className="reference-group">
              <button
                type="button"
                className="reference-row"
                onClick={() => {
                  setSafetyOpen(false);
                  setReportOpen(true);
                }}
              >
                Пожаловаться
              </button>
              <button type="button" className="reference-row" onClick={() => setConfirmRemove(true)}>
                Удалить из пар
              </button>
            </div>
          )}
          {error && (
            <p className="reference-error" role="alert">
              {error}
            </p>
          )}
        </PageDialog>
      )}
      {reportOpen && <ReportDialog userId={conversation.user.id} onClose={() => setReportOpen(false)} />}
    </section>
  );
}
